// frontend/src/Components/SyncTo/RecordModal.jsx
// Phase 30 — Recording UI. Renders:
//   - Main Modal: red pulse, mm:ss timer, level meter, Start/Pause/Resume/Cancel/Stop
//   - SYNC-04 sub-dialogs (mic denied, first silence, rolling silence, zero-byte failure)
//   - D-08 orphan prompt (secondary Modal listing recoverable sessions)
//
// ALL state + behaviour comes from useRecording() (Plan 30-04).
// This file is a render leaf — no effect hooks, no local business logic.
//
// Modal behaviour:
//   - backdrop='static' while recording (Pattern 8 — prevent accidental dismiss)
//   - keyboard=false while recording
//   - closeButton hidden while recording (only path off is Cancel/Stop)

import React from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { useRecording } from './RecordingProvider';
import './RecordModal.css';

function formatMMSS(ms) {
  if (!Number.isFinite(ms) || ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function RecordModal() {
  const rec = useRecording();
  if (!rec) return null;                                                   // provider not mounted (dev safety)

  const {
    state, timer, level, modalOpen, meta,
    start, pause, resume, stop, cancel, closeModal,
    micDeniedOpen,       dismissMicDenied,
    firstSilenceOpen,    dismissFirstSilence,
    rollingSilenceOpen,  dismissRollingSilence,
    zeroByteFailureOpen, dismissZeroByteFailure,
    orphans, dismissOrphanPrompt, downloadOrphan, discardOrphan, resumeOrphan,
  } = rec;

  const recording = state === 'recording' || state === 'paused' || state === 'starting' || state === 'stopping';
  const paused = state === 'paused';

  const handleStart  = async () => { try { await start(meta || {}); } catch { /* onMicDenied handled provider-side */ } };
  const handlePause  = () => pause();
  const handleResume = () => resume();
  const handleStop   = async () => { await stop(); };
  const handleCancel = async () => { await cancel(); };

  return (
    <>
      {/* ── Main recording Modal ── */}
      <Modal
        show={modalOpen}
        onHide={closeModal}
        size="lg"
        centered
        dialogClassName="responsive-modal"
        backdrop={recording ? 'static' : true}                            /* T-30-17 */
        keyboard={!recording}
      >
        <Modal.Header closeButton={!recording}>
          <div className="w-100">
            <Modal.Title className="mb-1">Record Session</Modal.Title>
            {meta?.patientLabel && (
              <span className="d-block text-muted small">{meta.patientLabel}</span>
            )}
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-center gap-3 mb-3">
            <span className={`syncto-pulse-dot${paused ? ' syncto-pulse-paused' : ''}`} />
            <span className="syncto-timer">{formatMMSS(timer)}</span>
            <span className="text-muted ms-auto small">
              {state === 'idle'       && 'Ready to record'}
              {state === 'starting'   && 'Requesting microphone…'}
              {state === 'recording'  && 'Recording'}
              {state === 'paused'     && 'Paused'}
              {state === 'stopping'   && 'Finishing…'}
            </span>
          </div>

          <div className="syncto-level-meter mb-3">
            <div
              className={`syncto-level-meter-bar${paused ? ' syncto-level-meter-paused' : ''}`}
              style={{ width: `${Math.min(100, Math.max(0, Math.round(level * 100)))}%` }}
              aria-label="Microphone level"
            />
          </div>

          <div className="d-flex justify-content-end gap-2">
            {state === 'idle' && (
              <Button variant="primary" onClick={handleStart}>
                <i className="fa-light fa-microphone-lines me-2" />Start
              </Button>
            )}
            {state === 'recording' && (
              <>
                <Button variant="outline-secondary" onClick={handlePause}>Pause</Button>
                <Button variant="outline-danger"  onClick={handleCancel}>Cancel</Button>
                <Button variant="danger"          onClick={handleStop}>Stop</Button>
              </>
            )}
            {state === 'paused' && (
              <>
                <Button variant="primary"         onClick={handleResume}>Resume</Button>
                <Button variant="outline-danger"  onClick={handleCancel}>Cancel</Button>
                <Button variant="danger"          onClick={handleStop}>Stop</Button>
              </>
            )}
            {(state === 'starting' || state === 'stopping') && (
              <Button variant="secondary" disabled>{state === 'starting' ? 'Starting…' : 'Stopping…'}</Button>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/* ── SYNC-04 sub-dialog: mic permission denied ── */}
      <Modal show={micDeniedOpen} onHide={dismissMicDenied} centered>
        <Modal.Header closeButton><Modal.Title>Microphone access needed</Modal.Title></Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-2">SyncTo needs microphone access to record this session.</Alert>
          <p className="mb-1">
            Click the lock or camera icon in your browser&apos;s address bar and allow microphone access for this site,
            then click Start again.
          </p>
          <p className="small text-muted mb-0">
            Chrome / Edge: Settings → Privacy and security → Site Settings → Microphone.{' '}
            Firefox: Preferences → Privacy &amp; Security → Permissions → Microphone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={dismissMicDenied}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* ── SYNC-04 sub-dialog: first-2s silence ── */}
      <Modal show={firstSilenceOpen} onHide={dismissFirstSilence} centered>
        <Modal.Header closeButton><Modal.Title>We can&apos;t hear you</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>It looks like your mic may be muted or misconfigured. Check your microphone and try again.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-danger" onClick={async () => { dismissFirstSilence(); await cancel(); }}>Stop</Button>
          <Button variant="primary" onClick={dismissFirstSilence}>Retry</Button>
        </Modal.Footer>
      </Modal>

      {/* ── SYNC-04 sub-dialog: rolling 30s silence warn ── */}
      <Modal show={rollingSilenceOpen} onHide={dismissRollingSilence} centered>
        <Modal.Header closeButton><Modal.Title>Still recording?</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>We haven&apos;t heard anything for 30 seconds. Keep recording or stop now — we&apos;ll auto-stop at 60 seconds of silence.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-danger" onClick={async () => { dismissRollingSilence(); await stop(); }}>Stop now</Button>
          <Button variant="primary" onClick={dismissRollingSilence}>Keep recording</Button>
        </Modal.Footer>
      </Modal>

      {/* ── PITFALLS #5 sub-dialog: 0-byte chunks ── */}
      <Modal show={zeroByteFailureOpen} onHide={dismissZeroByteFailure} centered>
        <Modal.Header closeButton><Modal.Title>Recording failed</Modal.Title></Modal.Header>
        <Modal.Body>
          <Alert variant="danger" className="mb-2">Your browser produced empty audio chunks for 25 seconds.</Alert>
          <p>This usually means the microphone is disconnected or the browser tab lost audio access. Please close this dialog and try again.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={dismissZeroByteFailure}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* ── D-08 orphan prompt ── */}
      <Modal show={(orphans?.length || 0) > 0} onHide={dismissOrphanPrompt} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Previous recording found</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="mb-3">We found {orphans.length} unfinished recording{orphans.length === 1 ? '' : 's'} from a previous session. Download and save, or discard.</p>
          <ul className="list-unstyled m-0">
            {orphans.map((o) => (
              <li key={o.sessionKey} className="border-bottom py-2 d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                  <div className="fw-semibold">{o.patientLabel || '(no patient label)'}</div>
                  <div className="text-muted small">
                    Started {new Date(o.startedAt).toLocaleString()} — ~{Math.max(1, Math.round(((o.lastSeq + 1) * 5) / 60))} min of audio
                  </div>
                </div>
                <Button size="sm" variant="primary"         onClick={() => resumeOrphan(o.sessionKey)}>Resume</Button>
                <Button size="sm" variant="outline-primary" onClick={() => downloadOrphan(o.sessionKey)}>Download</Button>
                <Button size="sm" variant="outline-danger"  onClick={() => discardOrphan(o.sessionKey)}>Discard</Button>
              </li>
            ))}
          </ul>
          <p className="text-muted small mt-3 mb-0">
            Note: &quot;Resume&quot; downloads the existing audio — continuing recording where you left off will arrive in a later release.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={dismissOrphanPrompt}>Dismiss all</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
